import { forwardRef, memo, useState, MouseEvent, Fragment, useEffect, useRef, ReactNode, useMemo, ForwardedRef } from 'react'
import AutoCompleteChipInput from 'components/AutoCompleteChipInput'
import { FormValueInput, Nullable } from 'types'
import { useSkillsAutocomplete } from 'hooks/useSkillAutocomplete'
import SkillsInputItem from 'components/SkillsInput/components/SkillsInputItem'
import { IAutoCompleteOption } from 'components/AutoCompleteTextField'
import SkillsInputPopover from './SkillsInputPopover'

export interface Skill {
  id: string
  name: string
  experience: number
}

export interface ISkillsInputProps {
  selectedIndustryId?: string
  input: FormValueInput<Skill[]>
  hintText?: ReactNode
  label?: string
  placeholder?: string
  disabled?: boolean
  fullWidth?: boolean
}

export interface IProfileSkillAutoCompleteOption extends IAutoCompleteOption {
  value: string
  experience?: Nullable<number>
}

// TODO remove these when we unifify the skill input components
function getSkillLabel(skill: IProfileSkillAutoCompleteOption): string {
  let label = skill.label

  if (skill.experience) {
    label += ` (${skill.experience}+ yrs)`
  }

  return label
}
// TODO create a shared, generic SkillsInput that can be reused across job and profile editing
const SkillsInput = (props: ISkillsInputProps, ref: ForwardedRef<HTMLInputElement>) => {
  const { input, hintText, selectedIndustryId, ...restProps } = props

  const skillsAutocomplete = useSkillsAutocomplete(selectedIndustryId ? [selectedIndustryId] : [])
  const [popoverSkill, setPopoverSkill] = useState<Skill | null>(null)
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null)
  const [autoOpenPopover, setAutoOpenPopover] = useState<boolean>(false)
  const lastChipRef = useRef<HTMLDivElement>(null)
  const chipCount = input?.value?.length || 0

  const autocompleteValue = useMemo<IProfileSkillAutoCompleteOption[]>(() => {
    return input.value?.map(skill => ({ value: skill.id, label: skill.name, experience: skill.experience })) || []
  }, [input.value])

  const handleChipClick = (e: MouseEvent<HTMLElement>, skill: IProfileSkillAutoCompleteOption) => {
    const convertedSkill = { id: skill.value, name: skill.label, experience: skill.experience || 1 }
    setPopoverSkill(convertedSkill)
    setPopoverAnchor(e.target as HTMLElement)
  }

  const handlePopoverClose = () => {
    setPopoverSkill(null)
    setPopoverAnchor(null)
  }

  const handleChange = (skills: IProfileSkillAutoCompleteOption[]) => {
    if (skills.length > chipCount) {
      // new skill added, enable auto-open popover for it
      // the actual opening is processed later as an effect, this merely enables the effect to run
      setAutoOpenPopover(true)
    }

    const convertedSkills : Skill[] = skills.map(skill => ({ id: skill.value, name: skill.label, experience: skill.experience || 1 }))

    input?.onChange(convertedSkills)
  }

  const handlePopoverSkillChange = (skill: Skill) => {
    const prevSkills = input?.value || []
    const index = prevSkills.findIndex(s => s.id === skill.id)

    setPopoverSkill(skill)

    if (index !== -1) {
      prevSkills.splice(index, 1, skill)

      input?.onChange(prevSkills)
    }
  }

  // auto-open popover for newly added skill (last skill)
  useEffect(() => {
    if (autoOpenPopover && chipCount > 0 && lastChipRef.current) {
      lastChipRef.current.click()
    }
  }, [chipCount, autoOpenPopover, lastChipRef.current])

  return (
    <Fragment>
      <AutoCompleteChipInput<IProfileSkillAutoCompleteOption>
        data-cy="chipfield-input-user_skills"
        {...skillsAutocomplete}
        value={autocompleteValue}
        ref={ref}
        onChange={handleChange}
        helperText={hintText}
        {...restProps}
        renderChip={(chipProps, skill: IProfileSkillAutoCompleteOption) => (
          <SkillsInputItem
            {...chipProps}
            ref={lastChipRef} // when multiple chips are present, the last one overrides earlier chip refs
            label={getSkillLabel(skill)}
            editable
            open={popoverSkill?.id === skill?.value}
            onClick={e => handleChipClick(e, skill)}
            data-cy="skills-input-chip"
          />
        )}
      />

      {popoverSkill && popoverAnchor && (
        <SkillsInputPopover
          popoverAnchor={popoverAnchor}
          skill={popoverSkill}
          onClose={handlePopoverClose}
          onChange={handlePopoverSkillChange}
          data-cy="chipfield-input-user_skills-popover"
        />
      )}
    </Fragment>
  )
}

export default memo(forwardRef<HTMLInputElement, ISkillsInputProps>(SkillsInput))
